import FilterList from '@/components/content/FilterList'
import { Controller, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { DATE_FORMAT } from '@/utils/consts'
import { CATEGORY_GLOBAL_SETTLEMENT_FLAG, SettlementAmountSummaryReq } from '@/services/settlement/settlementManagement/types'
import { useGetCountries } from '@/services/commonCode/useCommonCodeQueries'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { Utils } from '@/utils/Utils'
import { Select } from '@/components/content/Select'
import { MonthDatePicker } from './MonthDatePicker'

export const initialFormValues: SettlementAmountSummaryReq = {
  settlementMonth: dayjs().format(DATE_FORMAT.dateMonth),
  // LP9182 — country 미지정 = 전체(미전송). '' 가 아닌 undefined 로 둬야 qsStringify 가 제거(BE 400 방지)
  country: undefined,
}

interface SettlementManagementFormProps {
  defaultValues?: SettlementAmountSummaryReq
  isLoading?: boolean
  maxDate?: Date
  onSearch: (formValues: SettlementAmountSummaryReq) => void
}
export function SettlementManagementForm({ isLoading, defaultValues, maxDate, onSearch }: SettlementManagementFormProps) {
  const { control, handleSubmit, reset, watch } = useForm<SettlementAmountSummaryReq>({
    defaultValues: { ...initialFormValues, ...defaultValues },
  })

  // LP9182 국가 차원 노출 flag. OFF 면 국가 드롭다운 미노출(레거시 동일)
  const { data: isCategoryGlobalSettlementEnabled } = useFeatureFlag(CATEGORY_GLOBAL_SETTLEMENT_FLAG)

  // LP9182 국가 드롭다운 — useGetCountries(moms BE 원천, KR/JP). 중국은 BE 미지원이라 자동 미노출. 단가관리(#442/#450) 통일
  // flag OFF 면 조회 자체 차단(no-op·실패 알럿 방지)
  const { data: countries } = useGetCountries(!!isCategoryGlobalSettlementEnabled)
  const countryOptions = useMemo(() => Utils.createSelectOptions(countries ?? [], 'name', 'code'), [countries])

  const handleReset = () => {
    reset(initialFormValues)
  }

  return (
    <FilterList
      list={[
        [
          {
            label: '정산 월',
            required: true,
            validationValue: watch('settlementMonth') ?? '',
            value: (
              <Controller
                name="settlementMonth"
                control={control}
                render={({ field: { value, onChange } }) => <MonthDatePicker value={value} onChange={onChange} maxDate={maxDate} inputWidth={368} />}
              />
            ),
          },
          // LP9182 flag OFF 면 국가 필터 미노출
          ...(isCategoryGlobalSettlementEnabled
            ? [
                {
                  label: '국가',
                  // 전체 = placeholder + onClear(undefined). 빈문자열 옵션 금지(layer Select)이므로 단가관리와 동일하게 표현
                  value: (
                    <Controller
                      name="country"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <div style={{ width: 180 }}>
                          <Select
                            value={value}
                            onValueChange={onChange}
                            onClear={() => onChange(undefined)}
                            options={countryOptions}
                            placeholder="전체"
                          />
                        </div>
                      )}
                    />
                  ),
                },
              ]
            : []),
        ],
      ]}
      isLoading={isLoading}
      onReset={handleReset}
      onSearch={handleSubmit(onSearch)}
    />
  )
}
